import { useEffect, useRef, useState } from "react";
import { AppError } from "../../../../stores/models/errors/AppError";
import { ITermsOfServiceFormValues } from "../../../authentication/_interfaces/ITermsOfServiceFormValues";
import { VolunteerRegistrationFlowExternalState, ViewVolunteersStepValues } from "../_interfaces/IAdminVolunteerRegistrationStepTypeValues";
import { IAdminVolunteerRegistrationFlowValues } from "../_interfaces/IAdminVolunteerRegistrationFlowValues";
import { AdminVolunteerRegistrationValidation } from "../validation/AdminVolunteerRegistrationValidation";
import { useRootStore } from "../../../../shared/general/hooks";
import { TableController } from "../../../../stores/models/TableController";
import { IVolunteerOverview } from "../../../../stores/models/VolunteerOverview";
import { OptionCollection } from "../../../../stores/models/OptionCollection";
import { StubOrganizationVolunteer } from "../../../../stores/models/StubOrganizationVolunteer";
import { VolunteerDetails } from "../../../../stores/models/VolunteerDetails";
import { Shift } from "../../../../stores/models/Shift";
import { AppErrors } from "../../../../stores/models/constants/AppErrors";

export function useViewVolunteersController(
    values: ViewVolunteersStepValues,
    allValues: IAdminVolunteerRegistrationFlowValues,
    externallyManagedState: VolunteerRegistrationFlowExternalState,
    update: (patch: Partial<ViewVolunteersStepValues>) => void,
    onSuccess: (override?: Partial<ViewVolunteersStepValues>) => void
): {
    // Values that affect the overall flow state
    values: ViewVolunteersStepValues,
    // Values that only need to persist for this particular step:
    stepBoundValues: { tableController: TableController<'id', IVolunteerOverview, StubOrganizationVolunteer, VolunteerDetails> },
    onChange: (patch: Partial<ViewVolunteersStepValues>) => void,
    onSubmit: (override?: Partial<ViewVolunteersStepValues>) => void,
    isSubmitting: boolean,
    error: string | undefined,
    fieldErrors: Partial<Record<keyof ViewVolunteersStepValues, string>>
} {

    const rootStore = useRootStore();

    const [error, setError] = useState<string>();
    const [isSubmitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ITermsOfServiceFormValues, string>>>({});
    const [validationRun, setValidationRun] = useState(false);
    const [isOneOffShift] = useState(allValues.offsets.length === 0);
    const [tableController] = useState(new TableController<'id', IVolunteerOverview, StubOrganizationVolunteer, VolunteerDetails>({
        records: new OptionCollection(
            'id',
            allValues.volunteersTableController?.persistentSelections?.selectedOptions || [],
            allValues.volunteersTableController?.persistentSelections?.selectedOptions
        ),
    }));

    // Set initial flow values:

    useEffect(() => {
        let patch: Partial<ViewVolunteersStepValues> = {};
        if (values.backClicked) {
            patch = {
                backClicked: false,
                loading: false,
                succeeded: false,
            }
        }
        update(patch);
    }, []);

    // Effects:

    // Close dialog upon successful registration
    useEffect(() => {
        if (values.succeeded) {
            externallyManagedState.dialogState.setOpen(false);
        }
    }, [values.succeeded]);

    // Keep volunteer selections in sync across steps
    useEffect(() => {
        update({ volunteerIds: tableController.records.selectedOptions.map(volunteer => volunteer.id) })
        if (allValues.volunteersTableController) {
            tableController.records.options.forEach(viewVolunteersSelection => {
                const matchingOption = allValues.volunteersTableController!.records?.options.find(option => option.object[tableController.records.identifier] === viewVolunteersSelection.object[allValues.volunteersTableController!.records.identifier]);
                if (matchingOption) {
                    const optionNeedsToggling = matchingOption.selected !== viewVolunteersSelection.selected;
                    if (optionNeedsToggling) {
                        allValues.volunteersTableController?.toggleOptionSelection(matchingOption);
                    }
                }
                if (viewVolunteersSelection.selected) {
                    allValues.volunteersTableController?.persistentSelections?.addOrUpdateOption(viewVolunteersSelection.object, viewVolunteersSelection.selected);
                } else {
                    allValues.volunteersTableController?.persistentSelections?.removeOption(viewVolunteersSelection.object);
                }
            })
        }
    }, [tableController.records.selectedOptions.length])

    useEffect(() => {
        if (validationRun && !isSubmitting) {
            const errors = validate().errors;
            setFieldErrors(errors);
        }
    }, [allValues.volunteerIds, validationRun])


    const validate = (override?: Partial<ViewVolunteersStepValues>) => {
        return AdminVolunteerRegistrationValidation({ ...externallyManagedState, ...allValues, ...override });
    }

    const isValidSubmission = (override?: Partial<ViewVolunteersStepValues>) => {
        return validate(override).valid;
    }

    const submit = async (override?: Partial<ViewVolunteersStepValues>) => {
        setSubmitting(true);
        setError(undefined);
        setValidationRun(true);

        const effectiveValues = {
            ...allValues,
            ...override
        };

        try {
            const shouldSwitchStepsWithoutValidation = effectiveValues.backClicked;
            if (shouldSwitchStepsWithoutValidation) { // If the flow should move to a different step
                update({ ...override });
                onSuccess(override);
            } else if (isValidSubmission(effectiveValues)) {
                externallyManagedState.dialogState.setLoading(true);
                update({ loading: true });
                let result: Shift | undefined;
                if (isOneOffShift) {
                    result = await rootStore.shiftStore.registerVolunteers(
                        effectiveValues.volunteerIds,
                        externallyManagedState.topLevelShift.id,
                        effectiveValues.offsets
                    );
                }
                update({ ...override, loading: false, succeeded: result !== undefined });
                onSuccess({ ...override, loading: false, succeeded: result !== undefined });
                if (allValues.onVolunteersRegistered && result) {
                    allValues.onVolunteersRegistered(result);
                }
            }
        } catch (err) {
            if (err instanceof AppError) {
                switch (err.code) {
                    case AppErrors.LIMIT_EXCEEDED.code:
                        setError("There are not enough remaining slots.")
                        break;
                    default:
                        setError(err.message);
                }
            } else {
                setError("An unknown error occurred.");
            }
        } finally {
            externallyManagedState.dialogState.setLoading(false);
            update({ loading: false });
            setSubmitting(false);
        }
    };

    // Set up onClick handler:

    const submitRef = useRef(submit);

    useEffect(() => {
        submitRef.current = submit;
    }, [submit]);

    useEffect(() => {
        const handler = () => {
            submitRef.current();
        };

        const backHandler = () => {
            submitRef.current({ backClicked: true });
        };

        externallyManagedState.dialogState.setPrimaryButton({
            children: isOneOffShift ? "Register" : "Next",
            onClick: handler
        })

        externallyManagedState.dialogState.setLeftButton({
            children: "Back",
            onClick: backHandler
        });

        externallyManagedState.dialogState.setHideCancelButton(false);
    }, []);

    return {
        values,
        stepBoundValues: {
            tableController
        },
        onChange: update,
        onSubmit: submit,
        isSubmitting,
        error,
        fieldErrors
    };
}